import {
  BulkVerificationRunResponse,
  EntityServiceAPI,
} from 'api/EntityServiceAPI';
import CreditsAPI from 'api/CreditsAPI';
import type { VerificationCheckErrorCategory } from './businessVerification.service';

/** Which Database Entities a Bulk Verification Run covers. */
export interface BulkVerificationRunScope {
  databaseId: string;
  /** Omitted for the whole database. At most 5,000. */
  databaseEntityIds?: string[];
}

export interface NothingToVerifyRecord {
  databaseEntityId: string;
  name: string | null;
}

/** What a run would do and cost. A shortfall is data here, never a refusal. */
export interface BulkVerificationRunPreview {
  total: number;
  toVerify: number;
  /** `records` holds at most the first 100. */
  nothingToVerify: { count: number; records: NothingToVerifyRecord[] };
  creditsRequired: number;
  creditsRemaining: number;
}

export interface StartedBulkVerificationRun {
  runId: string;
  databaseId: string;
  status: 'running';
  total: number;
  nothingToVerify: number;
  creditsRequired: number;
  creditsRemaining: number;
  createdAt: string;
}

export interface BulkVerificationRunFailure {
  databaseEntityId: string;
  reason: VerificationCheckErrorCategory;
}

export interface BulkVerificationRunView {
  runId: string;
  databaseId: string;
  requestedBy: string;
  scope: 'list' | 'database';
  status: 'running' | 'completed';
  total: number;
  completed: number;
  failed: number;
  skipped: number;
  creditsReserved: number;
  createdAt: string;
  completedAt?: string;
  /** At most 100. */
  failures: BulkVerificationRunFailure[];
}

export type BulkVerificationRunRefusal =
  | 'no-credits'
  | 'in-progress'
  | 'nothing-to-verify'
  | 'unknown-ids'
  | 'addon-required'
  | 'database-off'
  | 'verification-disabled'
  | 'unavailable'
  | 'not-found'
  | 'unreachable';

export interface BulkVerificationRunError {
  refusal: BulkVerificationRunRefusal;
  status: number;
  code?: string;
  message: string;
  /** `{ required, remaining }` on a credit shortfall. */
  details?: unknown;
  /** The unknown ids (up to 20) on a 400. */
  errors?: unknown;
}

export type BulkVerificationRunOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; error: BulkVerificationRunError };

type BulkVerificationRunApi = Pick<
  typeof EntityServiceAPI,
  | 'previewBulkVerificationRun'
  | 'startBulkVerificationRun'
  | 'getBulkVerificationRun'
  | 'getActiveBulkVerificationRun'
>;

const REFUSALS_BY_CODE: Record<string, BulkVerificationRunRefusal> = {
  VERIFICATION_CREDITS_INSUFFICIENT: 'no-credits',
  RUN_IN_PROGRESS: 'in-progress',
  NOTHING_TO_VERIFY: 'nothing-to-verify',
  ENTITY_ADDON_REQUIRED: 'addon-required',
  DATABASE_VERIFICATION_OFF: 'database-off',
  VERIFICATION_DISABLED: 'verification-disabled',
  MEMBERSHIP_UNAVAILABLE: 'unavailable',
  BULK_RUN_UNAVAILABLE: 'unavailable',
};

const refusalFor = (
  response: BulkVerificationRunResponse,
): BulkVerificationRunRefusal => {
  const byCode = response.code ? REFUSALS_BY_CODE[response.code] : undefined;
  if (byCode) return byCode;
  if (response.status === 400 && Array.isArray(response.errors)) {
    return 'unknown-ids';
  }
  if (response.status === 404) return 'not-found';
  if (response.status === 0) return 'unreachable';
  return 'unavailable';
};

const outcomeOf = async <T>(
  call: () => Promise<BulkVerificationRunResponse | undefined>,
  read: (result: any) => T = (result) => result as T,
): Promise<BulkVerificationRunOutcome<T>> => {
  let response: BulkVerificationRunResponse | undefined;
  try {
    response = await call();
  } catch {
    response = undefined;
  }
  if (!response) {
    response = { success: false, status: 0, result: '' };
  }
  if (response.success) return { ok: true, value: read(response.result) };
  return {
    ok: false,
    error: {
      refusal: refusalFor(response),
      status: response.status,
      code: response.code,
      message:
        typeof response.result === 'string' && response.result
          ? response.result
          : 'The verification service did not respond.',
      details: response.details,
      errors: response.errors,
    },
  };
};

export const previewBulkVerificationRun = (
  scope: BulkVerificationRunScope,
  api: BulkVerificationRunApi = EntityServiceAPI,
): Promise<BulkVerificationRunOutcome<BulkVerificationRunPreview>> =>
  outcomeOf(() => api.previewBulkVerificationRun(scope));

/** Reserves every credit the run needs, then queues it. */
export const startBulkVerificationRun = (
  scope: BulkVerificationRunScope,
  api: BulkVerificationRunApi = EntityServiceAPI,
): Promise<BulkVerificationRunOutcome<StartedBulkVerificationRun>> =>
  outcomeOf(() => api.startBulkVerificationRun(scope));

/** A run's progress. Runs are kept for 7 days, then answer `not-found`. */
export const getBulkVerificationRun = (
  runId: string,
  api: BulkVerificationRunApi = EntityServiceAPI,
): Promise<BulkVerificationRunOutcome<BulkVerificationRunView>> =>
  outcomeOf(() => api.getBulkVerificationRun(runId));

/** The database's run in progress, or null when there is none. */
export const getActiveBulkVerificationRun = (
  databaseId: string,
  api: BulkVerificationRunApi = EntityServiceAPI,
): Promise<BulkVerificationRunOutcome<BulkVerificationRunView | null>> =>
  outcomeOf(
    () => api.getActiveBulkVerificationRun(databaseId),
    (result) => result?.run ?? null,
  );

export interface VerificationCredits {
  allowance: number;
  usedThisMonth: number;
  /** ISO end of the active credit period, or null when there is no real one. */
  periodEnd: string | null;
}

/** The team's monthly verification allowance and use, or null when unreadable. */
export const loadVerificationCredits = async (
  teamId: string,
): Promise<VerificationCredits | null> => {
  try {
    const response = await CreditsAPI.getVerificationCreditsSummary(teamId);
    if (!response?.success) return null;
    return {
      allowance: Number(response.result?.verification_credits ?? 0),
      usedThisMonth: Number(response.result?.used_this_month ?? 0),
      periodEnd: response.result?.period_end ?? null,
    };
  } catch {
    return null;
  }
};
